import { Button } from "@/components/ui/button";
import { chatClose, chatEmoji, chatGif, chatInfo, chatPlus } from "@/constants";
import { ScrollArea, ScrollBar } from "../ui/scroll-area";
import MessageCard from "../cards/Profile/MessageCard";
import { useEffect, useRef, useState } from "react";
import ChatRoomDrawer from "../drawers/ChatRoomDrawer";
import { toast } from "react-toastify";
import { RoomMessage, UserProfile } from "@/types";
import useUserStore from "@/stores/authentication/store";
import LoginDialog from "@/pages/Login/LoginDialog";
import useChatSocketStore from "@/stores/socket/chat.store";
import EmojiPicker from "emoji-picker-react";

interface ChatRoomProps {
  isOpen: boolean;
  handleClose: () => void;
  fullScreen?: boolean;
}

function useClickOutside(ref: any, onClick: () => void) {
  useEffect(() => {
    function handleClickOutside(event: any) {
      if (ref.current && !ref.current.contains(event.target)) {
        onClick();
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [ref]);
}

const ChatRoom = ({ isOpen, handleClose, fullScreen }: ChatRoomProps) => {
  const emojiPopupRef = useRef(null);

  const { isAuthenticated, data: userData } = useUserStore();
  const currentUserId = userData?.id;
  const [isMessagesVisible, setIsMessagesVisible] = useState(true);

  const [message, setMessage] = useState<string>("");
  const [messagesList, setMessagesList] = useState<RoomMessage[]>([]);
  const [activeUserCount, setActiveUserCount] = useState<number>(0);
  const [onlineUsers, setOnlineUsers] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isEmojiOpen, setIsEmojiOpen] = useState(false);

  const chatAreaRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    chatAreaRef.current?.scrollIntoView(false);
  };

  useEffect(() => {
    scrollToBottom();
  }, [messagesList]);

  useClickOutside(emojiPopupRef, () => setIsEmojiOpen(false));

  const { socket } = useChatSocketStore();

  useEffect(() => {
    // Ensure socket exists before setting up event listeners
    if (socket) {
      // Define event handlers
      const handleOnlineUsersInRoom = (data: { users: string[] }) => {
        // setActiveUserCount(data.users.length);
      };

      const handleMessages = (data: RoomMessage[]) => {
        scrollToBottom();
        setMessagesList(data);
      };

      const handleServerMsg = (data: RoomMessage) => {
        scrollToBottom();
        setMessagesList((prev) => [...prev, data]);
      };

      const handleOnlineUsers = (data: string[]) => {
        setOnlineUsers(data);
        // setActiveUserCount(data.length);
      };

      const handleOnlineUserProfiles = (data: UserProfile[]) => {
        setActiveUserCount(data.length);
      };

      // Attach event listeners
      socket.on("onlineUsersInRoom", handleOnlineUsersInRoom);
      socket.on("messages", handleMessages);
      socket.on("serverMsg", handleServerMsg);
      socket.on("onlineUsers", handleOnlineUsers);
      socket.on("onlineUserProfiles", handleOnlineUserProfiles);

      // Cleanup function to remove event listeners
      return () => {
        socket.off("onlineUsersInRoom", handleOnlineUsersInRoom);
        socket.off("messages", handleMessages);
        socket.off("serverMsg", handleServerMsg);
        socket.off("onlineUsers", handleOnlineUsers);
        socket.off("onlineUserProfiles", handleOnlineUserProfiles);
      };
    }
  }, [socket]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (error) {
      return;
    }

    if (message.trim() !== "") {
      if (!socket) return alert("Socket not connected");

      socket.emit(
        "clientMsg",
        { msg: message, room: "f2e8fbd5-2897-4a25-a8d1-ffcb53b94abf" },
        (response: { success: boolean; error?: string | undefined }) => {
          if (response && response.success) {
          } else {
            toast.error("Failed to send message");
            console.error(
              "Failed to send message:",
              response ? response.error : "Unknown error",
            );
          }
        },
      );
      setMessage("");
    }
  };

  useEffect(() => {
    if (isOpen) {
      setIsMessagesVisible(true);
    }
  }, [isOpen]);

  /*  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === "Enter" && !e.shiftKey) {
          e.preventDefault(); // Prevent default behavior of form submission on Enter
          handleSubmit(e);
        } else if (e.key === "Enter" && e.shiftKey) {
          /!* empty *!/
        }
      };*/

  const handleChange = (value: string) => {
    setMessage(value);

    if (value.length > 500) {
      setError("Max length exceeded.");
    } else {
      setError(null);
    }
  };

  const openInNewWindow = () => {
    window.open("live-chat", "_blank", "width=340,height=600");
  };

  return (
    <ChatRoomDrawer
      isOpen={isOpen}
      onClose={handleClose}
      isMessagesVisible={isMessagesVisible}
      fullScreen={fullScreen}
    >
      <div className="relative h-full bg-[#050523] text-white">
        {/* radial gradien at center */}
        <div className="radial-gradient-chatbar-drawer"></div>

        {/* header */}
        <div className="flex justify-between gap-4 bg-[#062268] bg-cover p-4">
          {/* left side */}
          <div className="flex items-center gap-4">
            <div className="flex flex-col">
              <span className="font-kanit text-base font-bold">Chat room </span>
              <div className="flex items-center gap-1 font-montserrat text-[12px] font-medium leading-5">
                {/*<div className="">*/}
                {/*  <img src={greenOnline} alt="online" />*/}
                {/*</div>*/}
                {/*<span className="text-[#00FFC2]">{activeUserCount}</span>*/}
                {/* <span className="inline-block size-2 rounded-full bg-[#00FFC2]"></span> */}
                {/* <span className="font-montserrat text-xs font-bold text-[#00FFC2]"> */}
                {/*   {activeUserCount} online */}
                {/* </span> */}
              </div>
            </div>
          </div>
          {/* right side */}
          {/*<Button*/}
          {/*  onClick={() => setIsMessagesVisible(!isMessagesVisible)}*/}
          {/*  className={`cancelDraggable size-10 bg-[#0C143F] p-2 sm:hidden`}*/}
          {/*>*/}
          {/*  <img*/}
          {/*    src={isMessagesVisible ? arrowDown : arrowUp}*/}
          {/*    alt="down-arrow"*/}
          {/*    className="min-w-3 "*/}
          {/*  />*/}
          {/*</Button>*/}
          {/*<Button*/}
          {/*  onClick={handleClose}*/}
          {/*  className={`size-10 bg-[#0C143F] p-1 max-sm:hidden`}*/}
          {/*>*/}
          {/*  <img src={chatClose} alt="down-arrow" />*/}
          {/*</Button>*/}
          <div className={`flex justify-between gap-2`}>
            <Button
              className={`size-10 bg-[#1181E833] p-0`}
              onClick={openInNewWindow}
            >
              <img src={chatInfo} alt="chat" />
            </Button>
            <Button
              className={`size-10 bg-[#1181E833] p-0`}
              onClick={handleClose}
            >
              <img src={chatClose} alt="chat" />
            </Button>
          </div>
        </div>
        {isMessagesVisible && (
          <>
            {/* messages */}
            <ScrollArea className="p-2 [height:calc(100%-140px)]">
              <div className="flex flex-col gap-4" ref={chatAreaRef}>
                {messagesList.map((mssg) => (
                  <MessageCard
                    key={mssg.id}
                    avatarSrc={mssg.sender.profilePicture || ""}
                    firstName={mssg.sender.firstName}
                    lastName={mssg.sender.lastName}
                    time={mssg.createdAt.toString()}
                    message={mssg.censoredText}
                    userName={mssg.sender.userName}
                    currentUser={mssg.senderId === currentUserId}
                    userOnline={onlineUsers.includes(mssg.senderId)}
                  />
                ))}
              </div>
              <ScrollBar orientation="vertical" />
            </ScrollArea>

            {/* send message */}
            {isAuthenticated ? (
              <form onSubmit={handleSubmit}>
                <div className="flex justify-around p-1">
                  <div
                    className={`mx-4 flex w-full rounded-lg bg-[#1181E840] p-1`}
                  >
                    <input
                      type="text"
                      placeholder="Your message ..."
                      className="flex-grow bg-transparent p-2 text-[#5DA9E9] placeholder-[#5DA9E9] outline-none"
                      value={message}
                      onChange={(e: any) => handleChange(e.target.value)}
                    />
                    <div className="hidden items-center gap-2 lg:flex">
                      {/* GIF Icon */}
                      {/* <a href="#" className="border-2"> */}
                      {/*   <img src={chatGif} alt="GIF" /> */}
                      {/* </a> */}

                      {/* Emoji Icon */}
                      <div
                        className="border-2"
                        onClick={() => setIsEmojiOpen(!isEmojiOpen)}
                      >
                        <img src={chatEmoji} alt="Emoji" />
                      </div>
                      <a></a>
                    </div>
                  </div>

                  {/* Plus Icon */}
                  {/* <Button className="size-12" type="submit"> */}
                  {/*   <img src={chatPlus} alt="send" className="min-w-12" /> */}
                  {/* </Button> */}
                </div>
              </form>
            ) : (
              <>
                <div className="mt-3 items-center justify-center px-3 text-center">
                  <LoginDialog
                    initialActiveButton="login"
                    trigger={
                      <Button className="z-10 h-8 w-full bg-white py-[14px] font-ubuntuMono text-sm font-bold text-[#00154A] hover:bg-white sm:h-[46px] md:px-[30px] md:font-sans md:text-[15px] md:font-bold md:leading-[18px]">
                        Sign In To Comment
                      </Button>
                    }
                  />
                </div>
              </>
            )}
          </>
        )}
        <div ref={emojiPopupRef}>
          <EmojiPicker
            open={isEmojiOpen}
            className="absolute -left-[2rem] -top-[32rem]"
            onEmojiClick={(e) => handleChange(message + e.emoji)}
          />
        </div>
      </div>
    </ChatRoomDrawer>
  );
};

export default ChatRoom;
